import { useDiagnoseTankReading } from '@/api/hooks/useTankReadings'
import { SystemType } from '@/api/types'
import { useTenantStore } from '@/stores/tenantStore'
import { formatCompactDateTime } from '@/utils/dateFormatters'
import { WarningOutlined } from '@ant-design/icons'
import { Alert, Button, Descriptions, Modal, Spin, Tag, theme } from 'antd'

interface TankReadingsDiagnoseModalProps {
  open: boolean
  onClose: () => void
  siteSourceId: string
  tankId: string
  /** Defaults to Gravitate S&D, which is also the backend's mapping default. */
  sourceSystem?: string
}

const YesNo = ({ value }: { value: boolean }) => (
  <Tag color={value ? 'green' : 'red'}>{value ? 'Yes' : 'No'}</Tag>
)

/** A value that is absent in a way the user should notice. */
const Missing = ({ label }: { label: string }) => {
  const { token } = theme.useToken()
  return (
    <>
      <WarningOutlined style={{ color: token.colorWarning, marginRight: 6 }} />
      {label}
    </>
  )
}

/** Pull the API's `detail` off an error body without widening to `any`. */
function errorText(error: unknown): string {
  if (error && typeof error === 'object' && 'detail' in error) {
    const { detail } = error as { detail: unknown }
    if (typeof detail === 'string') return detail
  }
  return 'Diagnostics could not be run. Check the console for details.'
}

export function TankReadingsDiagnoseModal({
  open,
  onClose,
  siteSourceId,
  tankId,
  sourceSystem = SystemType.SND,
}: TankReadingsDiagnoseModalProps) {
  const myTenant = useTenantStore().selectedTenant?.name ?? 'Your tenant'
  const { data, isFetching, error } = useDiagnoseTankReading(
    { siteSourceId, tankId, sourceSystem },
    open,
  )

  // Null whenever the chain breaks before the owning tenant is resolved.
  const otherTenant = data?.expect_readings_from_tenant ?? 'Partner'
  const reading = data?.latest_tank_reading
  const mappedToMaster = Boolean(
    data?.master_site_tank?.goid && data?.master_site_tank?.tank_goid,
  )

  return (
    <Modal
      open={open}
      onCancel={onClose}
      // Wide enough that a tenant name in the label column doesn't wrap.
      width={1000}
      title={`Tank Reading Diagnostics: Tank ${tankId}`}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {isFetching && <Spin />}

      {!isFetching && error && (
        <Alert type='error' showIcon message={errorText(error)} />
      )}

      {!isFetching && !error && data && (
        <>
          {data.message && (
            <Alert
              type='warning'
              showIcon
              message={data.message}
              className='mb-3'
            />
          )}
          <Descriptions
            column={1}
            size='small'
            bordered
            labelStyle={{ width: 550 }}
          >
            <Descriptions.Item label={`${myTenant} tank mapped to master`}>
              <YesNo value={mappedToMaster} />
            </Descriptions.Item>
            <Descriptions.Item label={`${otherTenant} sending tank readings`}>
              <YesNo value={data.send_enabled} />
            </Descriptions.Item>
            <Descriptions.Item label={`${myTenant} receiving tank readings`}>
              <YesNo value={data.receive_enabled} />
            </Descriptions.Item>
            <Descriptions.Item label={`${myTenant} receiving instances`}>
              {data.receiving_instances.length > 0 ? (
                data.receiving_instances.map((name) => (
                  <Tag key={name}>{name}</Tag>
                ))
              ) : (
                <Missing label='None' />
              )}
            </Descriptions.Item>
            <Descriptions.Item label='Latest tank reading'>
              {reading ? (
                `${reading.volume} ${reading.unit} — ${formatCompactDateTime(reading.read_time)}`
              ) : (
                <Missing label='Never' />
              )}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </Modal>
  )
}
